import { useCallback, useEffect, useRef, useState } from 'react'
import { getTemplateLinks } from './templateLinkApi'
import { NO_TEMPLATE_LINKS, readTemplateLinks } from './templateLinks'

/**
 * Reads the template links of a workflow (E1) when it opens or changes identity (story 224, CU5).
 * A failure of E1 never blocks the editor (S106): the result is simply "no links".
 *
 * @param {number} workflowId id of the top-level piece workflow open in the editor (0 or falsy: nothing to read)
 * @param {number} reloadKey read again whenever it changes, also with the same id: a save in place keeps the id, but the pending
 * template updates it carried changed the links (adjustment 2026-10-29)
 * @returns {{latestVersion: boolean, links: object[], reload: function(): Promise<void>}}
 */
export const useWorkflowTemplateLinks = (workflowId = 0, reloadKey = 0) => {
  const [state, setState] = useState(NO_TEMPLATE_LINKS)
  const mountedRef = useRef(true)
  const requestRef = useRef(0)

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const reload = useCallback(async () => {
    const request = ++requestRef.current
    let next = NO_TEMPLATE_LINKS
    if (workflowId) {
      try {
        next = readTemplateLinks(await getTemplateLinks(workflowId))
      } catch (error) {
        next = NO_TEMPLATE_LINKS
      }
    }
    // A late answer of a previous workflow must not overwrite the current one.
    if (mountedRef.current && request === requestRef.current) {
      setState(next)
    }
    // `reloadKey` is not read inside: changing it is what makes a new `reload`, and so a new read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, reloadKey])

  useEffect(() => {
    reload()
  }, [reload])

  return { ...state, reload }
}
